import {useCallback, useEffect, useRef} from 'react'
import {useForm} from 'react-hook-form'
import {useIntl} from 'react-intl'

import useNewsletterSignUp from './use-newsletter-sign-up'
import useOcapiSite from './useOcapiSite'
import {useToast} from './use-toast'

/**
 * Form state and confirmation behaviour for the one-step Leap newsletter views.
 *
 * @example
 * const {form, submitForm, recaptchaRef, isSubscribed, confirmationProps} =
 *     useNewsletterForm({source})
 *
 * <NewsletterForm form={form} submitForm={submitForm} recaptchaRef={recaptchaRef} />
 *
 * @param {Object} options
 * @param {string} options.source - Sign-up source sent to the backend.
 * @returns {Object} Newsletter form state, submit handler, reCAPTCHA ref and confirmation state.
 */
const useNewsletterForm = ({source}) => {
    const {recaptchaRef, signUp} = useNewsletterSignUp({source})
    const confirmationRef = useRef(null)
    const {newsletterPreselected} = useOcapiSite()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const form = useForm({
        mode: 'onTouched',
        defaultValues: {
            optIn: Boolean(newsletterPreselected)
        }
    })
    const isSubscribed = form.formState.isSubmitSuccessful

    useEffect(() => {
        if (isSubscribed) confirmationRef.current?.focus()
    }, [isSubscribed])

    const confirmationProps = {
        ref: confirmationRef,
        role: 'status',
        tabIndex: -1,
        className: 'leap-newsletter-confirmation'
    }

    const submitForm = useCallback(
        async ({email, pregnantDueDate}) => {
            form.clearErrors('global')
            try {
                await signUp({email, ...(pregnantDueDate && {pregnantDueDate})})
            } catch {
                form.setError('global', {type: 'server'})
                toast({
                    title: formatMessage({
                        id: 'newsletter-subscription.error.generic',
                        defaultMessage: 'There was an error subscribing to newsletter!'
                    }),
                    status: 'error'
                })
            }
        },
        [form, formatMessage, signUp, toast]
    )

    return {form, submitForm, recaptchaRef, isSubscribed, confirmationProps}
}

export default useNewsletterForm
